import { useQuery } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';
import type { AxiosError } from 'axios';

import { FEATURE } from '~/common/enums/feature.enum';
import { STALE_TIME, CACHE_TIME } from '~/common/constants/react-query.const';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { getAssignedBonuses } from '~/services/bonuses/bonuses.service.api';
import { bugSnagLog } from '~/utils/bugsnag';
import type { NormalizedBonus } from '~/domains/promotions/types/bonus.types';

import { DepositBonusQueryKeys } from '../constants/query';
import { normalizeBonuses } from '../utils/transformers';

export interface UseDepositBonusesParams {
  /** Page number (1-indexed) */
  page?: number;
  /** Number of items per page */
  pageSize?: number;
  /** Whether the query should be enabled */
  enabled?: boolean;
}

export interface NormalizedBonusesResponse {
  data: NormalizedBonus[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

// Currently used only for deposit view
// @TODO: Refactor in the future?
export const useDepositBonuses = ({
  page = 1,
  pageSize = 10,
  enabled = true,
}: UseDepositBonusesParams = {}) => {
  const { t } = useTranslation('promotions');
  const isDepositMatchBonusEnabled = useIsFeatureEnabled(FEATURE.ENABLE_DEPOSIT_MATCH_BONUS);

  return useQuery<NormalizedBonusesResponse, Error>({
    queryKey: DepositBonusQueryKeys.ASSIGNED({ page, pageSize }),
    queryFn: async () => {
      const response = await getAssignedBonuses({ page, pageSize });
      // Normalize bonuses for UI display
      const normalizedData = normalizeBonuses(response.data);
      return {
        data: normalizedData,
        page: response.page,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
      };
    },
    staleTime: STALE_TIME.DEFAULT, // 5 minutes - bonuses don't change frequently
    cacheTime: CACHE_TIME.DEFAULT, // 10 minutes - reasonable cache time
    keepPreviousData: true,
    enabled: enabled && isDepositMatchBonusEnabled,
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error?.response?.data?.message || t('depositBonus.error.description');

      // Show user-friendly error notification
      showNotification({
        title: t('depositBonus.error.title'),
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });

      // Log error for debugging
      bugSnagLog({
        errorMessage: error.message,
        context: 'useDepositBonuses hook',
        severity: 'error',
        errorDetails: error,
      });
    },
  });
};
